import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Loader2, ChevronRight, ChevronLeft, Globe } from 'lucide-react';

const API = `${(typeof window !== 'undefined' ? window.location.origin : '')}/api`;
const LANGS = ['en', 'fr', 'es'];

export default function MultiLocationPublic() {
    const { slug } = useParams();
    const { t, i18n } = useTranslation();
    const [data, setData] = useState(null);
    const [error, setError] = useState(false);
    const [loading, setLoading] = useState(true);
    const [idx, setIdx] = useState(0);
    const lang = LANGS.includes(i18n.language) ? i18n.language : 'en';

    useEffect(() => {
        axios.get(`${API}/multi-location/public/${slug}`)
            .then((r) => setData(r.data))
            .catch(() => setError(true))
            .finally(() => setLoading(false));
    }, [slug]);

    // Optional: warm the wallpaper fetch as soon as its URL is known (cleanly, once per URL).
    const bgUrl = data?.design?.background_image_url;
    useEffect(() => {
        if (!bgUrl) return;
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        link.href = bgUrl;
        document.head.appendChild(link);
        return () => { document.head.removeChild(link); };
    }, [bgUrl]);

    const tr = (obj, fallback = '') => (obj && (obj[lang] || obj.en)) || fallback;

    const goSelect = (choiceId) => {
        window.location.assign(`${API}/multi-location/public/${slug}/select?choice=${encodeURIComponent(choiceId)}`);
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-300" data-testid="mll-public-loading">
                <Loader2 className="w-8 h-8 animate-spin" />
            </div>
        );
    }
    if (error || !data) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-300 px-6 text-center" data-testid="mll-public-unavailable">
                <p className="text-lg">{t('reviewShield.multiLocation.unavailable', 'This link is not currently available.')}</p>
            </div>
        );
    }

    const d = data.design || {};
    const c = data.content || {};
    const locations = data.locations || [];
    const dark = d.display_mode !== 'light';
    const cardBg = d.card_style === 'solid'
        ? (dark ? 'bg-slate-900' : 'bg-white')
        : d.card_style === 'clear'
            ? 'bg-transparent'
            : (dark ? 'bg-white/10 backdrop-blur-xl' : 'bg-white/70 backdrop-blur-xl');
    const btnBase = d.button_style === 'outline'
        ? 'border border-current bg-transparent'
        : (dark ? 'bg-white/15 hover:bg-white/25' : 'bg-slate-900/90 hover:bg-slate-900 text-white');

    const bgStyle = {};
    // Immediate fallback so the page never flashes white while the wallpaper
    // (a separate, larger asset) is still downloading. The configured color wins;
    // otherwise a neutral tone matching the display mode sits behind the image.
    bgStyle.backgroundColor = d.background_color || (dark ? '#0f172a' : '#f1f5f9');
    if (d.background_image_url) {
        bgStyle.backgroundImage = `url(${d.background_image_url})`;
        bgStyle.backgroundSize = d.background_behavior === 'contain' ? 'contain' : (d.background_behavior === 'tile' ? 'auto' : 'cover');
        bgStyle.backgroundRepeat = d.background_behavior === 'tile' ? 'repeat' : 'no-repeat';
        bgStyle.backgroundPosition = d.background_position || 'center';
    }
    const logoH = { small: 'h-10', medium: 'h-16', large: 'h-24', very_large: 'h-32' }[d.logo_size || 'medium'];

    const LocationButton = ({ loc }) => (
        <button
            data-testid={`mll-location-${loc.choice_id}`}
            onClick={() => goSelect(loc.choice_id)}
            className={`w-full flex items-center justify-between gap-3 px-5 py-4 rounded-2xl text-left transition-colors ${btnBase} ${dark ? 'text-white' : 'text-slate-900'}`}
        >
            <span className="text-base font-medium">{tr(loc.label, loc.choice_id)}</span>
            <ChevronRight className="w-5 h-5 opacity-70 shrink-0" />
        </button>
    );

    return (
        <div className={`min-h-screen w-full flex flex-col items-center ${dark ? 'text-white' : 'text-slate-900'} ${!d.background_color && !d.background_image_url ? (dark ? 'bg-slate-950' : 'bg-slate-100') : ''}`}
             style={bgStyle} data-testid="mll-public-page">
            {/* language toggle */}
            <div className="w-full max-w-md flex justify-end pt-4 px-4">
                <div className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs ${dark ? 'bg-white/10' : 'bg-white/70'}`} data-testid="mll-lang-toggle">
                    <Globe className="w-3.5 h-3.5 opacity-70" />
                    {LANGS.map((l) => (
                        <button key={l} data-testid={`mll-lang-${l}`} onClick={() => i18n.changeLanguage(l)}
                            className={`px-1.5 rounded-full uppercase ${lang === l ? 'font-bold underline' : 'opacity-60'}`}>{l}</button>
                    ))}
                </div>
            </div>

            <div className="flex-1 w-full max-w-md flex flex-col justify-center px-5 py-8">
                {d.logo_url && (
                    <img src={d.logo_url} alt="" className={`${logoH} object-contain mx-auto mb-6`} data-testid="mll-logo" />
                )}
                <div className={`rounded-3xl p-6 ${cardBg}`}>
                    <h1 className="text-2xl font-bold text-center mb-2" data-testid="mll-heading">
                        {tr(c.heading, t('reviewShield.multiLocation.defaultHeading', 'Select your location'))}
                    </h1>
                    <p className="text-sm text-center opacity-80 mb-6" data-testid="mll-subtext">
                        {tr(c.subtext, t('reviewShield.multiLocation.defaultSubtext', 'Please tell us which location served you.'))}
                    </p>

                    {locations.length === 0 ? (
                        <p className="text-center text-sm opacity-70" data-testid="mll-no-locations">
                            {t('reviewShield.multiLocation.unavailable', 'This link is not currently available.')}
                        </p>
                    ) : data.selection_style === 'rolling_selector' ? (
                        <div className="flex flex-col items-center gap-4" data-testid="mll-rolling">
                            <div className="flex items-center gap-3 w-full">
                                <button data-testid="mll-roll-prev" onClick={() => setIdx((i) => (i - 1 + locations.length) % locations.length)}
                                    className={`p-2 rounded-full ${dark ? 'bg-white/10' : 'bg-black/10'}`}><ChevronLeft className="w-5 h-5" /></button>
                                <div className="flex-1"><LocationButton loc={locations[idx]} /></div>
                                <button data-testid="mll-roll-next" onClick={() => setIdx((i) => (i + 1) % locations.length)}
                                    className={`p-2 rounded-full ${dark ? 'bg-white/10' : 'bg-black/10'}`}><ChevronRight className="w-5 h-5" /></button>
                            </div>
                            <div className="flex gap-1.5">
                                {locations.map((_, i) => (
                                    <span key={i} className={`w-2 h-2 rounded-full bg-current ${i === idx ? '' : 'opacity-30'}`} />
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-3" data-testid="mll-buttons">
                            {locations.map((loc) => <LocationButton key={loc.choice_id} loc={loc} />)}
                        </div>
                    )}
                </div>
                <p className="text-center text-[11px] opacity-50 mt-6">Powered by YulOnTap</p>
            </div>
        </div>
    );
}
